import axios from "axios";
import {
  ICampaign,
  CampaignType,
  CampaignFilters,
  FilterOptions,
  Recipient,
  RecipientConsentState,
  RecipientCountResponse,
  CampaignReportSummary,
  CampaignReportEntriesPage,
  ReportStatusFilter,
  SelectedCustomer,
} from "../types/ICampaign";

const BASE_URL = `${process.env.REACT_APP_SERVER_URL}/api/v1/communications`;
const COMPANY_URL = `${process.env.REACT_APP_SERVER_URL}/api/v1/company`;

export const getCampaigns = async (): Promise<ICampaign[]> => {
  const response = await axios.get(`${BASE_URL}/campaigns`);
  return response.data.data;
};

/** One page of the list. Rows are list-shaped: no designer JSON, no body. */
export interface CampaignsPage {
  rows: ICampaign[];
  total: number;
  page: number;
  limit: number;
  pageCount: number;
}

export const getCampaignsPage = async (page: number, limit: number): Promise<CampaignsPage> => {
  const response = await axios.get(`${BASE_URL}/campaigns`, { params: { page, limit } });
  return response.data.data;
};

export const getCampaign = async (id: string): Promise<ICampaign> => {
  const response = await axios.get(`${BASE_URL}/campaigns/${id}`);
  return response.data.data;
};

export const createCampaign = async (data: {
  campaignType?: CampaignType;
  name: string;
  subject?: string;
  templateId?: string | null;
  body?: string | null;
  designJson?: Record<string, unknown> | null;
  messageText?: string | null;
  filters?: CampaignFilters;
}): Promise<ICampaign> => {
  const response = await axios.post(`${BASE_URL}/campaigns`, data);
  return response.data.data;
};

export const updateCampaign = async (
  id: string,
  data: Partial<{
    name: string;
    subject: string;
    templateId: string | null;
    body: string | null;
    designJson: Record<string, unknown> | null;
    messageText: string | null;
    filters: CampaignFilters;
  }>
): Promise<ICampaign> => {
  const response = await axios.put(`${BASE_URL}/campaigns/${id}`, data);
  return response.data.data;
};

export const deleteCampaign = async (id: string): Promise<void> => {
  await axios.delete(`${BASE_URL}/campaigns/${id}`);
};

export const resetCampaign = async (id: string): Promise<ICampaign> => {
  const response = await axios.post(`${BASE_URL}/campaigns/${id}/reset`);
  return response.data.data;
};

export const getRecipients = async (id: string): Promise<Recipient[]> => {
  const response = await axios.get(`${BASE_URL}/campaigns/${id}/recipients`);
  return response.data.data;
};

export const getRecipientCount = async (
  id: string
): Promise<RecipientCountResponse> => {
  const response = await axios.get(
    `${BASE_URL}/campaigns/${id}/recipient-count`
  );
  return response.data.data;
};

export const previewRecipientCount = async (
  filters: CampaignFilters,
  campaignType: CampaignType = "email"
): Promise<RecipientCountResponse> => {
  const response = await axios.post(`${BASE_URL}/campaigns/preview-count`, {
    filters,
    campaignType,
  });
  return response.data.data;
};

export const lookupRecipientContact = async (
  value: string,
  type: "email" | "phone"
): Promise<Recipient[]> => {
  const response = await axios.get(`${BASE_URL}/recipients/lookup`, {
    params: { value, type },
  });
  return response.data.data;
};

/** Consent of a bare typed address/number that matched no customer (ESERV2-1471). */
export const getRecipientContactState = async (
  value: string,
  type: "email" | "phone"
): Promise<RecipientConsentState> => {
  const response = await axios.get(`${BASE_URL}/recipients/state`, {
    params: { value, type },
  });
  return response.data.data;
};

export const previewRecipients = async (
  filters: CampaignFilters,
  campaignType: CampaignType = "email"
): Promise<Recipient[]> => {
  const response = await axios.post(`${BASE_URL}/campaigns/preview-recipients`, {
    filters,
    campaignType,
  });
  return response.data.data;
};

export const sendCampaign = async (id: string): Promise<ICampaign> => {
  const response = await axios.post(`${BASE_URL}/campaigns/${id}/send`);
  return response.data.data;
};

/**
 * Live counts for a campaign that is sending (ESERV2-1425).
 *
 * Deliberately not the full delivery report: every send-log row, at campaign
 * 63's 23,715 recipients, is far too heavy to poll. This reads the same
 * counts straight from the send log, which is also the only place they exist
 * while a send is running - the campaign's own total_sent is not written until
 * the whole send finishes.
 */
export interface CampaignProgress {
  status: string;
  total: number;
  sent: number;
  failed: number;
  queued: number;
  /** A delivery result can arrive while the send is still running. */
  bounced: number;
  complained: number;
  complete: boolean;
}

export const getCampaignProgress = async (id: string): Promise<CampaignProgress> => {
  const response = await axios.get(`${BASE_URL}/campaigns/${id}/progress`);
  return response.data.data;
};

export const scheduleCampaign = async (
  id: string,
  scheduledAt: string,
  timezone: string
): Promise<void> => {
  await axios.post(`${BASE_URL}/campaigns/${id}/schedule`, {
    scheduledAt,
    timezone,
  });
};

export const cancelCampaignSchedule = async (id: string): Promise<void> => {
  await axios.delete(`${BASE_URL}/campaigns/${id}/schedule`);
};

/**
 * The report in pages (ESERV2-1457 slice 3.6b). The old single call returned
 * every send-log row — 23,715 rows / 7.5 MB for Millard's campaign 63 — and
 * rendering them all froze the browser for seconds per interaction. The page
 * reads the header figures once and one page of rows at a time; that endpoint
 * and its client were removed on 2026-09-02.
 */
export const getCampaignReportSummary = async (
  id: string
): Promise<CampaignReportSummary> => {
  const response = await axios.get(`${BASE_URL}/campaigns/${id}/report/summary`);
  return response.data.data;
};

export const REPORT_PAGE_SIZE = 100;

export const getCampaignReportEntries = async (
  id: string,
  status: ReportStatusFilter,
  page: number,
  limit: number = REPORT_PAGE_SIZE
): Promise<CampaignReportEntriesPage> => {
  const response = await axios.get(`${BASE_URL}/campaigns/${id}/report/entries`, {
    params: { status, page, limit },
  });
  return response.data.data;
};

/** Streamed by the API; the browser only ever holds the finished file. */
export const downloadCampaignReportCsv = async (
  id: string,
  status: ReportStatusFilter
): Promise<void> => {
  const response = await axios.get(`${BASE_URL}/campaigns/${id}/report/export.csv`, {
    params: { status },
    responseType: "blob",
  });
  const url = URL.createObjectURL(response.data as Blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `campaign-report-${id}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

export const cloneCampaign = async (id: string): Promise<ICampaign> => {
  const response = await axios.post(`${BASE_URL}/campaigns/${id}/clone`);
  return response.data.data;
};

export const createFollowUp = async (
  id: string,
  statusFilter: "sent" | "failed" | "opened" | "not_opened"
): Promise<ICampaign> => {
  const response = await axios.post(`${BASE_URL}/campaigns/${id}/follow-up`, {
    statusFilter,
  });
  return response.data.data;
};

export const getFilterOptions = async (): Promise<FilterOptions> => {
  const response = await axios.get(`${BASE_URL}/filter-options`);
  return response.data.data;
};

export const getTemplates = async (): Promise<
  { id: string; name: string; subject: string; templateType: string; messageText: string | null }[]
> => {
  const response = await axios.get(`${BASE_URL}/templates`);
  return response.data.data;
};

export const searchCustomers = async (
  query: string
): Promise<SelectedCustomer[]> => {
  const response = await axios.get(`${COMPANY_URL}/customers`, {
    params: { type: "autoComplete", search: query },
  });
  return (response.data || []).map(
    (c: Record<string, unknown>) => ({
      id: Number(c.id),
      name: [c.first_name, c.last_name].filter(Boolean).join(" "),
      email: String(c.email || ""),
      companyName: c.company_name ? String(c.company_name) : undefined,
    })
  );
};

export const getCustomersByIds = async (
  ids: number[]
): Promise<SelectedCustomer[]> => {
  if (ids.length === 0) {
    return [];
  }
  // The company customers endpoint exposes a "LIST" type that returns the
  // business_customer rows whose id is in the provided ids (active only).
  // (There is no "byIds" type on the backend — using it falls through to the
  // default branch and returns a non-array, which crashed the filter modal.)
  const response = await axios.get(`${COMPANY_URL}/customers`, {
    params: { type: "LIST", ids },
  });
  const rows = Array.isArray(response.data) ? response.data : [];
  return rows.map((c: Record<string, unknown>) => ({
    id: Number(c.id),
    name: [c.first_name, c.last_name].filter(Boolean).join(" "),
    email: String(c.email || ""),
    companyName: c.company_name ? String(c.company_name) : undefined,
  }));
};
